import {
    getGridDateOperators,
    getGridNumericOperators,
    getGridSingleSelectOperators,
    getGridStringOperators,
    GRID_CHECKBOX_SELECTION_COL_DEF,
    GridColDef,
    ValueOptions,
} from '@mui/x-data-grid-pro';
import { TFunction } from 'i18next';
import formatDateWithBrowserLocale from '../../../core/legacy/utils/date';
import { NetworkSection } from '../../../core/domain/Network';
import { ViewSectionOnMapButton } from '../../network-map/ViewSectionOnMapButton';
import { ValueTypeToFilter } from '../../session/list-sessions/getDataGridColumns';
import { GlobalStateTag, TagVariant } from './GlobalStateTag';
import NetworkStringDisplay from './NetworkStringDisplay';
import { getGlobalStateTagVariant } from './utils';

const EMPTY_VALUE = '';

const getFilter = (type: ValueTypeToFilter) => {
    switch (type) {
        case 'string':
            return getGridStringOperators().filter(({ value }) => value !== 'isAnyOf');
        case 'direction':
            return getGridSingleSelectOperators().filter(({ value }) => value === 'is');
        case 'number':
            return getGridNumericOperators().filter(({ value }) => ['>', '<'].includes(value));
        default:
            return getGridDateOperators().filter(({ value }) => ['onOrAfter', 'before', 'is', 'not'].includes(value));
    }
};

export const getDataGridColumns = (t: TFunction, sortable: boolean): GridColDef[] => {
    const baseColDef = (align: GridColDef['align'] = 'right'): Partial<GridColDef> => ({
        align,
        headerAlign: 'center',
        cellClassName: 'light',
        sortable,
        filterable: true,
        hideable: true,
    });

    return [
        { ...GRID_CHECKBOX_SELECTION_COL_DEF, disableReorder: true, hideable: false },
        {
            ...baseColDef('left'),
            cellClassName: 'dark',
            display: 'flex',
            field: 'roadName',
            filterOperators: getFilter('string'),
            flex: 3,
            headerName: t('reference.roadSection.roadName'),
            minWidth: 100,
            renderCell: ({ row }) => NetworkStringDisplay({ data: row.roadName }),
        },
        {
            ...baseColDef(),
            field: 'name',
            headerName: t('reference.roadSection.name'),
            minWidth: 100,
            flex: 2,
            type: 'string',
            renderCell: ({ row }) => row.name || EMPTY_VALUE,
        },
        {
            ...baseColDef(),
            field: 'prd',
            headerName: t('reference.roadSection.prd'),
            minWidth: 100,
            flex: 2,
            type: 'string',
            renderCell: ({ row }) => row.prd || '',
        },
        {
            ...baseColDef(),
            field: 'abd',
            headerName: t('reference.roadSection.abd'),
            minWidth: 100,
            flex: 2,
            type: 'number',
            filterOperators: getFilter('number'),
            renderCell: ({ row }) => row.abd ?? null,
        },
        {
            ...baseColDef(),
            field: 'prf',
            headerName: t('reference.roadSection.prf'),
            minWidth: 100,
            flex: 2,
            type: 'string',
            renderCell: ({ row }) => row.prf || '',
        },
        {
            ...baseColDef(),
            field: 'abf',
            headerName: t('reference.roadSection.abf'),
            minWidth: 100,
            flex: 2,
            type: 'number',
            filterOperators: getFilter('number'),
            renderCell: ({ row }) => row.abf || null,
        },
        {
            ...baseColDef(),
            field: 'gestionary',
            headerName: t('reference.roadSection.gestionary'),
            minWidth: 150,
            flex: 2,
            type: 'string',
            renderCell: ({ row }) => row.gestionary || EMPTY_VALUE,
        },
        {
            ...baseColDef(),
            field: 'cumulStart',
            headerName: t('reference.roadSection.start'),
            minWidth: 100,
            sortable: true,
            hideable: false,
            type: 'number',
            filterOperators: getFilter('number'),
            renderCell: ({ row }) => `${row.cumulStart.toFixed(0)}`,
        },
        {
            ...baseColDef(),
            field: 'cumulEnd',
            headerName: t('reference.roadSection.end'),
            minWidth: 100,
            sortable: true,
            hideable: false,
            type: 'number',
            filterOperators: getFilter('number'),
            renderCell: ({ row }) => `${row.cumulEnd.toFixed(0)}`,
        },
        {
            ...baseColDef(),
            field: 'category',
            headerName: t('reference.roadSection.category'),
            minWidth: 100,
            flex: 2,
            type: 'string',
            renderCell: ({ row }) => row.category || EMPTY_VALUE,
        },
        {
            ...baseColDef(),
            field: 'overallRate',
            headerName: t('networkList.networkCondition.overallRate'),
            minWidth: 100,
            flex: 2,
            type: 'number',
            filterOperators: getFilter('number'),
            renderCell: ({ row }) => row.overallRate || '-',
        },
        {
            ...baseColDef(),
            field: 'overallState',
            headerName: t('networkList.networkCondition.overallState'),
            minWidth: 100,
            flex: 2,
            type: 'singleSelect',
            valueOptions: ['good', 'averageGood', 'averageBad', 'bad', 'unknown'],
            getOptionLabel: (value: ValueOptions) => {
                const variantTitles: Record<TagVariant, string> = {
                    good: 'labels.good',
                    averageGood: 'labels.averageGood',
                    averageBad: 'labels.averageBad',
                    bad: 'labels.bad',
                    unknown: 'labels.notScanned',
                };

                return t(variantTitles[value as TagVariant] || String(value));
            },
            filterOperators: getGridSingleSelectOperators(),
            valueGetter: (_, row) => {
                const variant: TagVariant = getGlobalStateTagVariant(row);

                return variant;
            },
            renderCell: ({ row }) => {
                const variant: TagVariant = getGlobalStateTagVariant(row);

                return GlobalStateTag({ variant });
            },
        },
        {
            ...baseColDef(),
            field: 'lastScanDate',
            headerName: t('networkList.networkCondition.lastScanDate'),
            minWidth: 150,
            flex: 2,
            type: 'date',
            filterOperators: getFilter('date'),
            valueGetter: (_, row) => (row.lastScanDate ? new Date(row.lastScanDate) : null),
            renderCell: ({ value }) => (value ? formatDateWithBrowserLocale(new Date(value), 'MM/yyyy') : EMPTY_VALUE),
        },
        {
            ...baseColDef(),
            field: 'pourcentageScanned',
            headerName: t('networkList.networkCondition.pourcentageScanned'),
            minWidth: 100,
            flex: 2,
            type: 'number',
            filterOperators: getFilter('number'),
            renderCell: ({ row }) => row.pourcentageScanned || EMPTY_VALUE,
        },
        {
            ...baseColDef(),
            field: 'surfaceRate',
            headerName: t('networkList.networkCondition.surfaceRate'),
            minWidth: 100,
            flex: 2,
            type: 'number',
            filterOperators: getFilter('number'),
            renderCell: ({ row }) => row.surfaceRate || '-',
        },
        {
            ...baseColDef(),
            field: 'structureRate',
            headerName: t('networkList.networkCondition.structureRate'),
            minWidth: 100,
            flex: 2,
            type: 'number',
            filterOperators: getFilter('number'),
            renderCell: ({ row }) => row.structureRate || '-',
        },
        {
            ...baseColDef(),
            field: 'length',
            headerName: t('reference.roadSection.length'),
            minWidth: 100,
            sortable: true,
            hideable: false,
            type: 'number',
            filterOperators: getFilter('number'),
            valueGetter: (_, row) => Math.abs(row.cumulEnd - row.cumulStart),
            renderCell: ({ value }) => `${value.toFixed(0)}`,
        },
        {
            ...baseColDef(),
            field: 'width',
            headerName: t('reference.roadSection.width'),
            minWidth: 100,
            flex: 2,
            type: 'number',
            filterOperators: getFilter('number'),
            renderCell: ({ row }) =>
                row.width?.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 2 }) ??
                EMPTY_VALUE,
        },
        {
            ...baseColDef(),
            field: 'surface',
            headerName: t('networkList.networkCondition.surface'),
            minWidth: 100,
            flex: 2,
            type: 'number',
            filterOperators: getFilter('number'),
            valueGetter: (_, row) => {
                if (row.width == null) {
                    return null;
                }

                return Math.abs((row.cumulEnd - row.cumulStart) * row.width);
            },
            renderCell: ({ value }) => (value != null && !Number.isNaN(value) ? `${value.toFixed(0)}` : EMPTY_VALUE),
        },
        {
            disableColumnMenu: true,
            disableReorder: true,
            field: 'actions',
            hideable: false,
            renderCell: ({ row }: { row: NetworkSection }) =>
                ViewSectionOnMapButton({ line: row.line, overallRate: row.overallRate }),
            resizable: false,
            type: 'actions',
            width: 60,
        },
    ];
};
